//
//  index.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module collects every page guide by help route.
//

import {
    settingsNavigationRoot,
    type ISettingsNavigationDestination
} from "src/routing/settingsNavigation";

import groupsPersonnel from "./groupsPersonnel";
import institutions from "./institutions";
import { cards, item, type PageGuide, type PageGuideItem } from "./pageGuide";
import settingsLookups from "./settingsLookups";
import settingsPages from "./settingsPages";
import trialConfiguration from "./trialConfiguration";
import trials from "./trials";

const guides: Record<string, PageGuide> = {
    ...settingsLookups,
    ...settingsPages,
    ...trials,
    ...trialConfiguration,
    ...institutions,
    ...groupsPersonnel
};

/**
 * Describes every settings card, at every level, from the page it opens.
 *
 * Postconditions: A section card lists the areas it opens; a page card uses the opening
 * sentence of that page's guide, falling back to the card's own description. Cards are in the
 * navigation's order, so the settings pages share one tour that visits the cards each page shows.
 */
const settingsCards = (destination: ISettingsNavigationDestination): PageGuideItem[] =>
    (destination.children ?? []).flatMap(child => [
        item(
            child.label,
            child.children?.length
                ? `${child.description ?? child.card.description} It opens ${child.children.map(grandchild => grandchild.label).join(", ")}.`
                : (guides[child.href]?.intro ?? child.card.description)
        ),
        ...settingsCards(child)
    ]);

guides["/settings"] = {
    ...guides["/settings"],
    stops: [cards(...settingsCards(settingsNavigationRoot)), ...guides["/settings"].stops]
};

/**
 * Every page guide keyed by the route that owns the page's help (see helpPages). A behaviour test
 * requires a guide for every page that owns its help.
 */
const pageGuides: Readonly<Record<string, PageGuide>> = guides;

export default pageGuides;
